import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import InfoIcon from '@mui/icons-material/Info'

import useTechAssistantChat, { ChatMessage } from 'hooks/useTechAssistantChat'

import ChatMessageList from 'components/Chatbot/ChatMessageList'
import ChatInputBar from 'components/Chatbot/ChatInputBar'
import ChatInfoBar from 'components/Chatbot/ChatInfoBar'
import SourceMaterialFilesList from 'components/Chatbot/SourceMaterialFilesList'
import Spinner from 'components/Spinner'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'

import { ConversationMessages, CitationFile } from 'types/Chat'

type Props = {
  remainingMessageCount: number | undefined
  isChatHome?: boolean
  handleToggleChats?: (message?: string) => void
  message?: string
  conversation?: ConversationMessages | null
  loadingConversation?: boolean
}

const GREETING = 'Hello! How can I help you?'

const Chatbot = ({ remainingMessageCount, isChatHome = false, handleToggleChats, message = '', conversation = null, loadingConversation = false }: Props) => {
  const { messages, sendMessage, citationFiles, connectionStatus, freeMessageCount, resetSession } = useTechAssistantChat(remainingMessageCount)
  const [inputValue, setInputValue] = useState(message)
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null)
  const [isSourceExpanded, setIsSourceExpanded] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const hasSentInitialMessage = useRef(false)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [sourceFiles, setSourceFiles] = useState<CitationFile[]>([])

  const displayMessages = useMemo(() => [
    ...chatMessages,
    ...messages.filter(m => m.content !== GREETING)
  ], [chatMessages, messages])

  const displayCitationFiles = useMemo(() => {
    const seen = new Set<string>()
    return [...sourceFiles, ...citationFiles].filter(f => {
      const key = f.s3Uri ?? f.fileLocation
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [sourceFiles, citationFiles])

  const isConnected = connectionStatus === 'connected'
  const isSearching = messages.some(msg => msg.searching)
  const canSend = Boolean(inputValue.trim()) && isConnected && !isSearching && (freeMessageCount === undefined || freeMessageCount > 0)

  useEffect(() => {
    setInputValue(message)
    hasSentInitialMessage.current = false
  }, [message])

  useEffect(() => {
    resetSession()
    setFeedback(null)
    if (conversation) {
      setChatMessages(
        [...conversation.messages]
          .sort((a, b) => Number(a.timestamp) - Number(b.timestamp))
          .map((msg) => ({
            id: msg.messageId,
            sender: msg.author === 'bot' ? 'Tech Assistant' : 'You' as const,
            content: msg.message,
            complete: true,
            responseBlockId: msg.responseBlockId
          }))
      )
      const files: CitationFile[] = []
      conversation.messages.forEach(msg => {
        msg.context?.forEach(citation => {
          citation.retrievedReferences?.forEach(ref => {
            if (ref.location?.s3Location?.uri) {
              files.push({
                cacheTitle: ref.metadata?.CacheTitle,
                fileLocation: ref.metadata?.FileLocation,
                s3Uri: ref.location.s3Location.uri,
                datePublished: ref.metadata?.DatePublished,
                litType: ref.metadata?.LitTypeDescription,
                model: ref.metadata?.Model,
                content: ref.content?.text
              })
            }
          })
        })
      })
      setSourceFiles(files)
    } else {
      setChatMessages([])
      setSourceFiles([])
    }
  }, [conversation, resetSession])

  const handleSend = useCallback(() => {
    if (!canSend) return
    const conversationId = conversation?.conversationId

    sendMessage(inputValue.trim(), conversationId)
    setInputValue('')

    inputRef.current?.focus()
    if (isChatHome) handleToggleChats?.(inputValue.trim())
  }, [canSend, conversation?.conversationId, sendMessage, inputValue, isChatHome, handleToggleChats])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [displayMessages])

  useEffect(() => {
    if (isConnected) inputRef.current?.focus()
  }, [isConnected])

  useEffect(() => {
    if (message && isConnected && !hasSentInitialMessage.current) {
      hasSentInitialMessage.current = true
      handleSend()
    }
  }, [message, isConnected, handleSend])

  const toggleFeedback = useCallback((type: 'up' | 'down') => {
    setFeedback(prev => prev === type ? null : type)
  }, [])

  return (
    <S.Container $isChatHome={isChatHome}>
      <S.Content $isChatHome={isChatHome}>
        {loadingConversation && <S.Loader> <Spinner variant="page" /> </S.Loader>}
        {!isChatHome && (
        <S.ChatBoxContent>
          <ChatMessageList ref={messagesEndRef} messages={displayMessages} isSourceExpanded={isSourceExpanded} />
          {!loadingConversation && displayCitationFiles.length > 0 && (
            <SourceMaterialFilesList
              isSourceExpanded={isSourceExpanded}
              onToggleSourceCallback={() => setIsSourceExpanded(!isSourceExpanded)}
              files={displayCitationFiles}
            />
          )}
        </S.ChatBoxContent>)}

        {
          freeMessageCount === 0 ? (
            <S.InfoText>
              <S.InfoIcon /> <span>You've reached the max number of messages per week. Please try again later. For unlimited access, ask your Partners Admin to upgrade your account to Diagnostics Premium.</span>
            </S.InfoText>
          )
            : (
              <>
                <ChatInfoBar
                  freeMessagesRemaining={freeMessageCount}
                  showFeedback={displayMessages.length > 1 && !isSearching}
                  feedback={feedback}
                  onToggleFeedback={toggleFeedback}
                />
                <ChatInputBar
                  ref={inputRef}
                  value={inputValue}
                  onChange={setInputValue}
                  onSend={handleSend}
                  isConnected={isConnected}
                  canSend={canSend}
                />
              </>
            )
        }

      </S.Content>
    </S.Container>
  )
}

export default Chatbot

const S = {
  Container: styled.div<{ $isChatHome: boolean }>`
    display: flex;
    flex-direction: column;
    height: ${({ $isChatHome }) => ($isChatHome ? '50%' : '80%')};
    width: ${({ $isChatHome }) => ($isChatHome ? 'auto' : '100%')};
    ${({ $isChatHome }) => !$isChatHome && `
      justify-content: flex-end;
      align-items: center;
    `}

    @media (${screenSize.small}) {
      ${({ $isChatHome }) => !$isChatHome && 'padding-top: 40px;'}
      flex-direction: row;
    }
  `,
  Loader: styled.div`
    & > div {
      margin: 0 auto;
    }
  `,
  Content: styled.div<{ $isChatHome: boolean }>`
    width: ${({ $isChatHome }) => ($isChatHome ? '100%' : '80%')};

    @media (${screenSize.small}) {
      width: 100%;
      padding: 0 3%;
    }
  `,
  ChatBoxContent: styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    padding-top: 16px;
    justify-content: flex-end;
  `,
  InfoText: styled.div`
    font-size: 14px;
    color: ${color('chatbotInfoText')};
    text-align: center;
    margin-top: 20px;
    display: flex;
    gap: 5px;
  `,
  InfoIcon: styled(InfoIcon)`
    vertical-align: middle;
    color: ${color('chatInfoIcon')};
  `
}
